/* @flow */
/* eslint-disable no-undef */
import React, { Component } from "react";
import WorkbenchIcons from "../WorkbenchIcon/WorkbenchIcons";
import type { $Coordinates } from "CoreComponents/MapProviders/Coordinates";
import Globals from "../../constants/Globals";
import "./JapanHereMaps.scss";
import { mapPropsAreDifferent } from '../../utils/MapUtils';
import type { $MapPadding } from 'CoreTypes/MapPadding';

type $Props = {
  map: {
    centralPoint?: ?$Coordinates,
    markers: Array<$Coordinates>,
    onDealerClick: Function,
    mapPadding?: $MapPadding
  },
  mapContainerId?: string
};

type $State = {
  scriptsAreLoaded: boolean
};

const clusterBaseClassName = `dlc-here-maps-icons__cluster-marker`;

export default class JapanHereMaps extends Component<$Props, $State> {
  platform = null;
  map = {};

  // This will be the markers layer reference in which we will add and remove objects
  clusterProvider = null;

  // This array will save the references for every object added in order to add and remove 'spider' objects, i.e.,
  // the markers and the spider legs
  spiderfiedObjects = [];

  // This will be used too reference the cluster that is spiderfied. It will allow to change its eventListeners
  // and styles
  spiderfiedCluster = null;

  state = {
    scriptsAreLoaded: false
  };

  async componentDidMount() {
    await loadHereMapsScripts();
    this.setState({
      scriptsAreLoaded: true
    });
    this.initMap();
    this.renderMarkers(
      this.props.map.markers,
      this.props.map.centralPoint || undefined
    );
  }

  UNSAFE_componentWillReceiveProps(nextProps: $Props) {
    if (
      Object.keys(this.map).length > 0 && //map is initialized
      /* $FlowFixMe */
      mapPropsAreDifferent(this.props.map, nextProps.map)
    ) {
      this.renderMarkers(
        nextProps.map.markers,
        nextProps.map.centralPoint || undefined
      );
    }
  }

  render() {
    const containerId = this.props.mapContainerId || "mapContainer";
    return this.state.scriptsAreLoaded ? (
      <div className="dl-leaflet" id={containerId} />
    ) : null;
  }

  initMap = () => {
    const H = window.H;
    const containerId = this.props.mapContainerId || "mapContainer";

    this.platform = new H.service.Platform({
      apikey: Globals.HERE_MAPS_TILES_URL.JP.API_KEY
    });

    const omvService = this.platform.getOMVService({
      path: Globals.HERE_MAPS_TILES_URL.JP.PATH
    });
    const baseUrl = Globals.HERE_MAPS_TILES_URL.JP.URL;

    // create a Japan specific style
    const style = new H.map.Style(`${baseUrl}normal.day.yaml`, baseUrl);

    // instantiate provider and layer for the basemap
    const omvProvider = new H.service.omv.Provider(omvService, style);
    const omvlayer = new H.map.layer.TileLayer(omvProvider, { max: 22 });

    // instantiate (and display) a map:
    this.map = new H.Map(document.getElementById(containerId), omvlayer, {
      imprint: null
    });

    // Instantiate the default behavior, providing the mapEvents object:
    // Add default map events (zoom , etc) to the map
    const mapEvents = new H.mapevents.MapEvents(this.map);
    new H.mapevents.Behavior(mapEvents);
    const ui = new H.ui.UI(this.map);
    var zoomControl = new H.ui.ZoomControl({ fractionalZoom: false });
    ui.addControl("zoom", zoomControl);
    // ui.getControl('zoom').setDisabled(false)
    window.addEventListener("resize", () => this.map.getViewPort().resize());

    // Build a separate provider/layer for the central point
    // The group with the circle objects will be reused
    const centralPointProvider = new H.map.provider.LocalObjectProvider();
    this.map.addLayer(new H.map.layer.ObjectLayer(centralPointProvider));
    const centralPointGroup = new H.map.Group();
    centralPointProvider.getRootGroup().addObject(centralPointGroup);

    // Build a layer for clusters
    const clusterProvider = this.getClusteredDataProvider()
    const markersLayer = new H.map.layer.ObjectLayer(clusterProvider)

    this.clusterProvider = clusterProvider;

    //Add layers in this oorder
    this.map.getLayers().add(markersLayer);

    // Every time the map changes, recalculate the position and size of the central point
    const self = this;
    this.map.addEventListener("mapviewchangeend", () => {
      const centralPoint = this.props.map.centralPoint;

      // Remove all previous central point circles
      centralPointGroup.removeAll();

      // Add a central point, if it exists
      if (centralPoint && centralPoint.lat && centralPoint.lng) {
        // Finally, add our circles to the map
        centralPointGroup.addObjects(this.renderCentralPoint(centralPoint));
      }

      self.unspiderfy()
    });
  };

  renderCentralPoint = (centralPoint: $Coordinates) => {
    const H = window.H;

    // Calculate pixel to map lat ratio
    // Get lat difference
    const mapBoundingBox = this.map
      .getViewModel()
      .getLookAtData()
      .bounds.getBoundingBox();
    const latDiff = mapBoundingBox.getRight() - mapBoundingBox.getLeft();
    // Calculate ratio
    // If the 10.000 multiplier is not used, our "radius" reference values would
    // have to be huge
    const ratio = (latDiff / this.map.getViewPort().width) * 10000;

    // These values mimic almost exactly the central point that we have on leaflet
    const innerCircleRadius = 50 * ratio;
    const outerCircleRadius = 300 * ratio;
    const centralPointColor = "rgba(0, 173, 239, 1)";

    const innerCircle = new H.map.Circle(centralPoint, innerCircleRadius, {
      style: {
        strokeColor: centralPointColor, // Color of the perimeter
        fillColor: centralPointColor,
        lineWidth: 1
      }
    });
    const outerCircle = new H.map.Circle(centralPoint, outerCircleRadius, {
      style: {
        strokeColor: centralPointColor, // Color of the perimeter
        fillColor: "rgba(0, 0, 0, 0)", // Color of the circle (transparent)
        lineWidth: 3
      }
    });

    return [outerCircle, innerCircle];
  };

  renderMarkers = (
    markers: Array<$Coordinates>,
    centralPoint?: $Coordinates
  ) => {
    const H = window.H;

    if (this.clusterProvider) this.clusterProvider.setDataPoints([])

    const dataPoints = [],
      group = new H.map.Group();

    for (let i = 0; i < markers.length; i++) {
      const originalMarker = markers[i];
      const dataPoint = new H.clustering.DataPoint(
        originalMarker.lat,
        originalMarker.lng,
        null,
        { outletId: originalMarker.outletId }
      );
      dataPoints.push(dataPoint);
      group.addObject(
        new H.map.Marker({ lat: originalMarker.lat, lng: originalMarker.lng })
      );
    }

    // Add the central point to the 'group' so that it is also visible
    // This will allow the bounding box calculation to account for the central point
    if (centralPoint && centralPoint.lat && centralPoint.lng) {
      group.addObject(
        new H.map.Marker({ lat: centralPoint.lat, lng: centralPoint.lng })
      );
    }

    // Create a layer that includes the data provider and its data points:
    if (this.clusterProvider) this.clusterProvider.addDataPoints(dataPoints)

    //Add a central point layer to the map
    this.setMapToBoundingBox(group.getBoundingBox());

    //Zoom to '18' if only one dealer is being shown
    //If this is not done, the map will zoom in to its max value
    if (markers.length === 1) this.map.setZoom(18);
  };

  // Since BoundingBox is a hereMaps type, remove Flow validation
  /* $FlowFixMe */
  setMapToBoundingBox = bb => {
    const mapPadding = this.props.map.mapPadding
      ? this.props.map.mapPadding
      : {
        top: Globals.MAPS_PADDING_DEFAULT,
        left: Globals.MAPS_PADDING_DEFAULT,
        bottom: Globals.MAPS_PADDING_DEFAULT,
        right: Globals.MAPS_PADDING_DEFAULT
      };
    this.map
      .getViewPort()
      .setPadding(
        mapPadding.top,
        mapPadding.right,
        mapPadding.bottom,
        mapPadding.left
      );
    this.map.getViewModel().setLookAtData(
      {
        bounds: bb
      },
      true
    );
  };

  unspiderfy = () => {
    const self = this
    const H = window.H

    if (this.spiderfiedCluster === null) return;
    const weight = this.spiderfiedCluster.getData().getWeight()

    // Remove spiderfied markers
    this.cleanSpiderfy()

    // Set the clusterMarker to normal
    // Set normal icon
    if (this.spiderfiedCluster != null) this.spiderfiedCluster.setIcon(
      new H.map.DomIcon(getClusterIcon(weight))
    )

    // Set normal eventListener
    if (this.spiderfiedCluster != null) this.spiderfiedCluster.addEventListener("tap", self.spiderfy);

    // Remove the spiderfied cluster reference
    this.spiderfiedCluster = null
  }

  cleanSpiderfy = () => {
    if (this.spiderfiedObjects.length > 0 && this.clusterProvider != null) {
      this.clusterProvider.getRootGroup().removeObjects(this.spiderfiedObjects)
    }
    this.spiderfiedObjects = []
  }

  spiderfy = (event: any) => {
    const self = this;
    const H = window.H
    const onDealerClick = this.props.map.onDealerClick

    event.stopPropagation()
    event.preventDefault()

    const clusterMarker = event.target
    const data = clusterMarker.getData();

    // Remove spiderfy eventListener from cluster to avoid multiple calls
    clusterMarker.removeEventListener("tap", self.spiderfy);
    // Add unspiderfy event to the clusterMarker
    clusterMarker.addEventListener("tap", self.unspiderfy)

    // Make the icon 'transparent'
    clusterMarker.setIcon(new H.map.DomIcon(getTransparentClusterIcon(data.getWeight())))

    // Clean previous spiderLayer
    this.cleanSpiderfy()

    // Generate positions for the spiderfied markers
    // We will use the clustermarker position as a reference point and add the marker on the top left and top
    // right of it

    // We need to do some calculations to add 'pixels' instead of latitude and longitude values using
    // geoToScreen to get the screen coordinates, add the distance values to them and only then give the
    // ScreenToGeo coordinates to the spiderfied markers

    let i = 0
    const clusterPosition = clusterMarker.getData().getPosition()
    const clusterScreenPosition = this.map.geoToScreen(clusterPosition)
    const UPPER_SCREEN_DISTANCE_PX = 20
    const RIGHT_SCREEN_DISTANCE_PX = 20

    const availableScreenPositions = [{
      //Top left
      x: clusterScreenPosition.x - RIGHT_SCREEN_DISTANCE_PX,
      y: clusterScreenPosition.y - UPPER_SCREEN_DISTANCE_PX
    },
    {
      //Top right
      x: clusterScreenPosition.x + RIGHT_SCREEN_DISTANCE_PX,
      y: clusterScreenPosition.y - UPPER_SCREEN_DISTANCE_PX
    }]

    const spiderfiedObjects = []

    data.forEachDataPoint(
      noisePoint => {
        const markerPosition = this.map.screenToGeo(
          availableScreenPositions[i].x,
          availableScreenPositions[i].y
        )
        const dealerMarker = buildCustomNoisePointPresentation(
          noisePoint,
          onDealerClick,
          markerPosition
        )

        // Generate the spider leg which will go from the cluster position too the marker position
        var linestring = new H.geo.LineString();
        linestring.pushPoint(markerPosition)
        linestring.pushPoint(clusterPosition)
        const spiderLeg = new H.map.Polyline(linestring)

        //Add both the marker and the spider leg
        spiderfiedObjects.push(dealerMarker)
        spiderfiedObjects.push(spiderLeg)
        i++
      }
    );

    if (this.clusterProvider != null) this.clusterProvider.getRootGroup().addObjects(spiderfiedObjects)

    this.spiderfiedCluster = clusterMarker;
    this.spiderfiedObjects = spiderfiedObjects
  }

  getClusteredDataProvider = () => {
    const H = window.H
    const self = this
    const onDealerClick = this.props.map.onDealerClick

    return new H.clustering.Provider([], {
      clusteringOptions: {
        eps: 64,
        minWeight: 2
      },
      theme: {
        getClusterPresentation: cluster => {
          const weight = cluster.getWeight();

          // Create an icon
          // Note that we create a different icon depending from the weight of the cluster
          // Create a marker for the cluster
          const clusterMarker = new H.map.DomMarker(cluster.getPosition(), {
            icon: new H.map.DomIcon(getClusterIcon(weight)),

            // Set min/max zoom with values from the cluster, otherwise
            // clusters will be shown at all zoom levels
            min: cluster.getMinZoom(),
            max: cluster.getMaxZoom()
          });

          // Bind cluster data to the marker
          clusterMarker.setData(cluster);

          if (weight === 2 && markersHaveTheSameLatAndLng(cluster)) {
            clusterMarker.addEventListener("tap", self.spiderfy);
          } else {
            clusterMarker.addEventListener("tap", () => {
              self.setMapToBoundingBox(cluster.getBoundingBox());
            });
          }
          return clusterMarker;
        },

        getNoisePresentation: function (noisePoint) {
          return buildCustomNoisePointPresentation(noisePoint, onDealerClick);
        }
      }
    });
  }
}

function buildCustomNoisePointPresentation(noisePoint, onDealerClick, position?) {
  const H = window.H
  const domIcon = `<div class="dlc-here-maps-icons__dl-dealer-marker">${WorkbenchIcons.ICO_DEALER_MARKER}</div>`;

  // Create a marker for noise points:
  const dealerMarker = new H.map.DomMarker(position ? position : noisePoint.getPosition(), {
    icon: new H.map.DomIcon(domIcon),
    min: position ? null : noisePoint.getMinZoom()
  });

  dealerMarker.addEventListener("tap", () => {
    onDealerClick(noisePoint.getData().outletId);
  });

  // dealerMarker.setData(noisePoint);
  return dealerMarker;
}

function markersHaveTheSameLatAndLng(cluster) {
  let result = true
  let targetLat, targetLng
  cluster.forEachDataPoint(
    dataPoint => {
      const { lat, lng } = dataPoint.getPosition()
      if (!targetLat || !targetLng) {
        targetLat = lat
        targetLng = lng
      } else {
        result = result && lat === targetLat && lng === targetLng
      }
    }
  )
  return result
}

function loadHereMapsScript(link: string) {
  return new Promise((resolve, reject) => {
    if (document.getElementById(link)) {
      resolve()
    } else {
      const script = document.createElement("script");
      script.id = link;
      script.src = link;
      document.body && document.body.appendChild(script);
      script.onload = resolve;
      script.onerror = reject;
    }
  });
}

function loadHereMapsCss() {
  const src = "https://js.api.here.com/v3/3.1/mapsjs-ui.css"
  if (document.getElementById(src)) return
  const head = document.getElementsByTagName("HEAD")[0];
  const link = document.createElement("link");
  link.id = src
  link.rel = "stylesheet";
  link.type = "text/css";
  link.href = src
  head.appendChild(link);
}

async function loadHereMapsScripts() {
  loadHereMapsCss();
  await loadHereMapsScript("https://js.api.here.com/v3/3.1.22.1/mapsjs-core.js");
  await loadHereMapsScript("https://js.api.here.com/v3/3.1.22.1/mapsjs-service.js");
  await loadHereMapsScript("https://js.api.here.com/v3/3.1.22.1/mapsjs-mapevents.js");
  await loadHereMapsScript("https://js.api.here.com/v3/3.1.22.1/mapsjs-clustering.js");
  await loadHereMapsScript("https://js.api.here.com/v3/3.1.22.1/mapsjs-ui.js");
}


function getClusterIcon(weight) {
  const domIcon = `<div class="${clusterBaseClassName}">
    <svg xmlns="http://www.w3.org/2000/svg">
      <circle class="${clusterBaseClassName}__outer-circle" cx="17" cy="17" r="16" />
      <circle class="${clusterBaseClassName}__inner-circle" cx="17" cy="17" r="13" />
      <text class="${clusterBaseClassName}__text" x="50%" y="50%" text-anchor="middle" dy=".3em">
        ${weight}
      </text>
    </svg>
  </div>`;

  return domIcon
}

function getTransparentClusterIcon(weight) {
  const spiderfiedDomIcon = `<div class="${clusterBaseClassName}">
          <svg xmlns="http://www.w3.org/2000/svg">
            <circle class="${clusterBaseClassName}__outer-circle__transparent" cx="17" cy="17" r="16" />
            <circle class="${clusterBaseClassName}__inner-circle__transparent" cx="17" cy="17" r="13" />
            <text class="${clusterBaseClassName}__text" x="50%" y="50%" text-anchor="middle" dy=".3em">
              ${weight}
            </text>
          </svg>
        </div>`

  return spiderfiedDomIcon
}
